import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiGlobe, FiLoader, FiMove, FiX } from 'react-icons/fi';
import { api } from '../services/api';
import AdminTranslationPopoverPortal from './AdminTranslationPopoverPortal';
import './AdminFieldTranslation.css';

let localeRequest;
const translationRequests = new Map();

if (typeof window !== 'undefined') {
  window.addEventListener('haryad:locales-changed', () => {
    localeRequest = null;
    translationRequests.clear();
  });
  window.addEventListener('haryad:translations-changed', (event) => {
    const entityType = event.detail?.entityType;
    const entityId = event.detail?.entityId;
    if (entityType && entityId) translationRequests.delete(`${entityType}:${entityId}`);
  });
}

const apiBaseByType = {
  product: '/api/products',
  article: '/api/articles',
  service: '/api/services',
  setting: '/api/settings',
};

const getNestedValue = (value, path) => path.split('.').reduce(
  (current, part) => (current === undefined || current === null ? undefined : current[part]),
  value
);

const printableValue = (value) => {
  if (Array.isArray(value)) return value.join('\n');
  return value === undefined || value === null ? '' : String(value);
};

const withNestedValue = (source, path, value) => {
  const clone = JSON.parse(JSON.stringify(source || {}));
  const parts = path.split('.');
  let current = clone;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      current[part] = value;
      return;
    }
    const nextIsIndex = /^\d+$/.test(parts[index + 1]);
    if (current[part] === undefined || current[part] === null) current[part] = nextIsIndex ? [] : {};
    current = current[part];
  });
  return clone;
};

const loadLocales = () => {
  if (!localeRequest) {
    localeRequest = api.get('/api/locales').then((response) => (
      Array.isArray(response.data) ? response.data : []
    )).catch((error) => {
      localeRequest = null;
      throw error;
    });
  }
  return localeRequest;
};

const loadTranslations = (entityType, entityId, { force = false } = {}) => {
  const key = `${entityType}:${entityId}`;
  if (force) translationRequests.delete(key);
  if (!translationRequests.has(key)) {
    const path = entityType === 'setting'
      ? '/api/settings/translations'
      : `${apiBaseByType[entityType]}/${entityId}/translations`;
    const request = api.get(path)
      .then((response) => (Array.isArray(response.data) ? response.data : []))
      .catch((error) => {
        translationRequests.delete(key);
        throw error;
      });
    translationRequests.set(key, request);
  }
  return translationRequests.get(key);
};

const emitSaveState = ({ entityType, entityId, locale, fieldPath, state }) => {
  if (typeof window === 'undefined') return;
  if (!window.__haryadTranslationSaveStates) window.__haryadTranslationSaveStates = {};
  const key = `${entityType}:${String(entityId)}:${locale}:${fieldPath}`;
  window.__haryadTranslationSaveStates[key] = state;
  window.dispatchEvent(new CustomEvent('haryad:translation-save-state', {
    detail: {
      entityType,
      entityId: String(entityId),
      locale,
      fieldPath,
      state,
    },
  }));
};

const AdminFieldTranslation = ({
  entityType,
  entityId,
  fieldPath,
  fieldLabel,
  sourceValue,
  multiline = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [value, setValue] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState('');
  const saveTimers = useRef({});
  const pendingValues = useRef({});
  const saveChains = useRef({});
  const requestNumbers = useRef({});
  const activeLocaleRef = useRef('');
  const rootRef = useRef(null);
  const mountedRef = useRef(true);

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = locales.find((locale) => locale.code === activeLocale);

  const resolveValue = (localeCode, items = translations) => {
    const translation = items.find((item) => item.locale === localeCode);
    const root = ['service', 'setting'].includes(entityType) ? translation?.data : translation;
    return printableValue(getNestedValue(root, fieldPath));
  };

  const selectLocale = (localeCode, items = translations) => {
    activeLocaleRef.current = localeCode;
    setActiveLocale(localeCode);
    // Empty when no translation yet — Booknetic-style blank field for new copy.
    setValue(resolveValue(localeCode, items));
    setSaveState('idle');
    setError('');
  };

  const handleOpen = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError('');
    try {
      const [localeItems, translationItems] = await Promise.all([
        loadLocales(),
        loadTranslations(entityType, entityId),
      ]);
      setLocales(localeItems);
      setTranslations(translationItems);
      const firstLocale = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
      selectLocale(firstLocale, translationItems);
    } catch (loadError) {
      setError(loadError.message || 'دریافت ترجمه این فیلد انجام نشد');
    } finally {
      setLoading(false);
    }
  };

  const persistPending = useCallback(async (localeCode) => {
    const pending = pendingValues.current[localeCode];
    if (!pending) {
      try {
        await saveChains.current[localeCode];
        return true;
      } catch (_) {
        return false;
      }
    }
    delete pendingValues.current[localeCode];
    if (saveTimers.current[localeCode]) {
      clearTimeout(saveTimers.current[localeCode]);
      delete saveTimers.current[localeCode];
    }
    if (mountedRef.current && activeLocaleRef.current === localeCode) setSaveState('saving');
    emitSaveState({ entityType, entityId, locale: localeCode, fieldPath, state: 'saving' });
    const path = entityType === 'setting'
      ? `/api/settings/translations/${localeCode}/field`
      : `${apiBaseByType[entityType]}/${entityId}/translations/${localeCode}/field`;
    const previous = saveChains.current[localeCode] || Promise.resolve();
    const request = previous
      .catch(() => undefined)
      .then(() => api.patch(path, { path: fieldPath, value: pending.value }));
    saveChains.current[localeCode] = request;

    try {
      await request;
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType, entityId },
      }));
      translationRequests.delete(`${entityType}:${entityId}`);
      if (mountedRef.current) {
        const freshItems = await loadTranslations(entityType, entityId, { force: true });
        setTranslations(freshItems.map((item) => (
          item.locale === localeCode ? { ...item, updatedAt: new Date().toISOString() } : item
        )));
        if (
          requestNumbers.current[localeCode] === pending.requestNumber
          && activeLocaleRef.current === localeCode
          && !pendingValues.current[localeCode]
        ) {
          setSaveState('saved');
        }
      }
      if (!pendingValues.current[localeCode]) {
        emitSaveState({ entityType, entityId, locale: localeCode, fieldPath, state: 'saved' });
      }
      return true;
    } catch (saveError) {
      emitSaveState({
        entityType,
        entityId,
        locale: localeCode,
        fieldPath,
        state: pendingValues.current[localeCode] ? 'waiting' : 'error',
      });
      if (
        mountedRef.current
        && requestNumbers.current[localeCode] === pending.requestNumber
        && activeLocaleRef.current === localeCode
      ) {
        setSaveState('error');
        setError(saveError.data?.message || saveError.message || 'ذخیره ترجمه انجام نشد');
      }
      return false;
    }
  }, [entityId, entityType, fieldPath]);

  const saveValue = (nextValue, localeCode) => {
    if (!localeCode) return;
    if (saveTimers.current[localeCode]) clearTimeout(saveTimers.current[localeCode]);
    const currentRequest = (requestNumbers.current[localeCode] || 0) + 1;
    requestNumbers.current[localeCode] = currentRequest;
    pendingValues.current[localeCode] = { value: nextValue, requestNumber: currentRequest };
    setSaveState('waiting');
    emitSaveState({ entityType, entityId, locale: localeCode, fieldPath, state: 'waiting' });
    setError('');
    setTranslations((items) => {
      const existing = items.find((item) => item.locale === localeCode);
      if (['service', 'setting'].includes(entityType)) {
        const updated = {
          ...(existing || { locale: localeCode }),
          data: withNestedValue(existing?.data, fieldPath, nextValue),
        };
        return existing ? items.map((item) => (item === existing ? updated : item)) : [...items, updated];
      }
      const updated = withNestedValue(existing || { locale: localeCode }, fieldPath, nextValue);
      return existing ? items.map((item) => (item === existing ? updated : item)) : [...items, updated];
    });
    saveTimers.current[localeCode] = setTimeout(() => persistPending(localeCode), 700);
  };

  useEffect(() => {
    const handleFlush = (event) => {
      const detail = event.detail || {};
      if (detail.entityType !== entityType || String(detail.entityId) !== String(entityId)) return;
      const localeCode = detail.locale;
      if (!localeCode) return;
      persistPending(localeCode);
    };
    window.addEventListener('haryad:flush-translations', handleFlush);
    return () => window.removeEventListener('haryad:flush-translations', handleFlush);
  }, [entityId, entityType, persistPending]);

  useEffect(() => {
    mountedRef.current = true;
    const timers = saveTimers.current;
    const pending = pendingValues.current;
    return () => {
      mountedRef.current = false;
      Object.values(timers).forEach(clearTimeout);
      Object.keys(pending).forEach((localeCode) => persistPending(localeCode));
    };
  }, [persistPending]);

  if (!entityId || !apiBaseByType[entityType]) return null;

  return (
    <span className="admin-field-translation" ref={rootRef}>
      <button
        type="button"
        className={`admin-field-translation__trigger ${open ? 'is-active' : ''}`}
        onClick={handleOpen}
        title={`ترجمه «${fieldLabel}»`}
        aria-label={`ترجمه ${fieldLabel}`}
        aria-expanded={open}
      >
        <FiGlobe aria-hidden="true" />
        <small>{enabledLocales.length || ''}</small>
      </button>
      {open && (
        <AdminTranslationPopoverPortal
          anchorRef={rootRef}
          className="admin-field-translation__popover"
          ariaLabel={`ترجمه ${fieldLabel}`}
          onClose={() => setOpen(false)}
        >
          {({ dragHandleProps }) => <>
          <span className="admin-field-translation__head" {...dragHandleProps}>
            <span>
              <strong>ترجمه {fieldLabel}</strong>
              <small>
                {entityType === 'setting'
                  ? 'پس از توقف تایپ، ترجمه این تنظیم به‌صورت خودکار ذخیره می‌شود.'
                  : `پس از توقف تایپ، پیش‌نویس خودکار ذخیره می‌شود؛ انتشار نهایی فقط از دکمهٔ ثابت پایین ویرایشگر ${entityType === 'article' ? 'مقاله' : entityType === 'service' ? 'خدمات' : 'محصول'} انجام می‌شود.`}
              </small>
              <small className="admin-translation-popover__drag-hint"><FiMove aria-hidden="true" /> برای جابه‌جایی بکشید</small>
            </span>
            <button type="button" className="ui-close-button" onClick={() => setOpen(false)} aria-label="بستن ترجمه"><FiX /></button>
          </span>

          {loading ? (
            <span className="admin-field-translation__loading"><FiLoader /> در حال دریافت زبان‌ها...</span>
          ) : (
            <>
              <span className="admin-field-translation__languages" role="tablist" aria-label="انتخاب زبان ترجمه">
                {enabledLocales.map((locale) => {
                  const filled = Boolean(resolveValue(locale.code));
                  const translation = translations.find((item) => item.locale === locale.code);
                  const stale = Boolean(translation?.isStale || translation?.isPublishedStale);
                  return (
                    <button
                      key={locale.code}
                      type="button"
                      role="tab"
                      className={`${activeLocale === locale.code ? 'is-active' : ''} ${filled ? 'is-filled' : 'is-empty'} ${stale ? 'is-stale' : ''}`}
                      title={stale ? 'متن فارسی پس از آخرین بازبینی این ترجمه تغییر کرده است' : undefined}
                      aria-selected={activeLocale === locale.code}
                      onClick={() => selectLocale(locale.code)}
                    >
                      <b>{locale.code.toUpperCase()}</b>
                      {locale.nativeName}
                      {filled ? <FiCheck aria-hidden="true" /> : null}
                      {stale ? <small>نیاز به بازبینی</small> : null}
                    </button>
                  );
                })}
              </span>
              {activeLocale && (
                <span className="admin-field-translation__editor" dir={activeLanguage?.direction || 'rtl'}>
                  <small className="admin-field-translation__source" dir="rtl">
                    متن فارسی: {printableValue(sourceValue) || '—'}
                  </small>
                  {multiline ? (
                    <textarea
                      rows="5"
                      value={value}
                      autoFocus
                      placeholder={value ? `ویرایش ترجمه ${activeLanguage?.name || activeLocale}...` : `ترجمه ${activeLanguage?.name || activeLocale} را اینجا بنویسید...`}
                      onChange={(event) => {
                        setValue(event.target.value);
                        saveValue(event.target.value, activeLocale);
                      }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={value}
                      autoFocus
                      placeholder={value ? `ویرایش ترجمه ${activeLanguage?.name || activeLocale}...` : `ترجمه ${activeLanguage?.name || activeLocale} را اینجا بنویسید...`}
                      onChange={(event) => {
                        setValue(event.target.value);
                        saveValue(event.target.value, activeLocale);
                      }}
                    />
                  )}
                  <small className={`admin-field-translation__status is-${saveState}`} dir="rtl">
                    {saveState === 'idle' && !value && 'فیلد خالی است؛ ترجمه را بنویسید تا خودکار ذخیره شود.'}
                    {saveState === 'waiting' && 'در انتظار توقف تایپ...'}
                    {saveState === 'saving' && <><FiLoader /> در حال ذخیره...</>}
                    {saveState === 'saved' && <><FiCheck /> پیش‌نویس ذخیره شد</>}
                    {saveState === 'error' && 'ذخیره نشد'}
                  </small>
                </span>
              )}
              {!enabledLocales.length && !error && (
                <small className="admin-field-translation__empty">زبان دیگری فعال نشده است.</small>
              )}
            </>
          )}
          {error && <small className="admin-field-translation__error">{error}</small>}
          </>}
        </AdminTranslationPopoverPortal>
      )}
    </span>
  );
};

export default AdminFieldTranslation;
